import { Brain } from 'lucide-react'
import type { AiqIndividualReportResponse } from '../../types/aiq-report'
import { ReportTabLayout } from './ReportTabLayout'
import { DifficultyBarChart } from './DifficultyBarChart'
import { TimeAnalysisChart } from './TimeAnalysisChart'
import { QuestionBreakdownTable } from './QuestionBreakdownTable'
import { ScoreSummaryCards } from './ScoreSummaryCards'

export interface AiqIndividualReportViewProps {
  data: AiqIndividualReportResponse
  productName?: string
  /** Optional back button to render at top-left */
  backButton?: React.ReactNode
}

/**
 * AIQ 개인 리포트 뷰 컴포넌트
 *
 * 순수 뷰 — API 호출 없음, 라우팅 없음.
 * 데이터를 받아서 차트와 테이블을 렌더링합니다.
 * 모든 앱(company, lc, b2c)에서 사용 가능.
 */
export function AiqIndividualReportView({ data, productName, backButton }: AiqIndividualReportViewProps) {
  const { summary, questions, byDomain, byDifficulty, timeAnalysis, standardReport, advancedReport, aiqIndexReport, hasPostAiq } = data
  const improvedCount = questions.filter(q => q.improved).length

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-linear-to-br from-indigo-600 via-purple-600 to-blue-700 text-white">
        <div className="max-w-5xl mx-auto px-4 py-8">
          {backButton && <div className="mb-6">{backButton}</div>}
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-14 h-14 bg-white/20 rounded-2xl">
              <Brain className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">AIQ 성장 리포트</h1>
              {productName && <p className="text-white/80 mt-1">{productName}</p>}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        {/* 1. Triangle Reports (Standard + Advanced) with Tabs */}
        <ReportTabLayout
          standardReport={standardReport}
          advancedReport={advancedReport}
          aiqIndexReport={aiqIndexReport}
          hasPostAiq={hasPostAiq}
        />

        {/* 2. Key Insight */}
        <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-xl">
          <p className="text-sm text-indigo-800">
            {hasPostAiq && improvedCount > 0
              ? `Pre-AIQ에서 틀렸던 문제 중 ${improvedCount}문제를 Post-AIQ에서 맞혔습니다.`
              : hasPostAiq
                ? 'Pre-AIQ와 Post-AIQ 결과를 비교 분석한 리포트입니다.'
                : 'Pre-AIQ 결과를 분석한 리포트입니다. Post-AIQ 완료 후 비교 분석이 가능합니다.'}
            {summary.preCompletedAt && summary.postCompletedAt && (
              <> ({new Date(summary.preCompletedAt).toLocaleDateString('ko-KR')} ~ {new Date(summary.postCompletedAt).toLocaleDateString('ko-KR')})</>
            )}
          </p>
        </div>

        {/* 3. Difficulty Bar Chart */}
        {hasPostAiq && <DifficultyBarChart data={byDifficulty} />}

        {/* 4. Time Analysis */}
        {hasPostAiq && <TimeAnalysisChart timeAnalysis={timeAnalysis} domainData={byDomain} />}

        {/* 5. Question Breakdown */}
        <QuestionBreakdownTable questions={questions} hasPostAiq={hasPostAiq} />

        {/* 6. 부록: 단순 정답 개수 */}
        <div className="border-t border-gray-200 pt-6">
          <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-4">부록: 단순 정답 개수</h3>
          <ScoreSummaryCards summary={summary} />
        </div>
      </div>
    </div>
  )
}
